import React, { useEffect, useState } from 'react';
import { createUserWithEmailAndPassword, deleteUser, sendEmailVerification, signInWithPopup } from 'firebase/auth';
import { deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db, googleProvider } from '../../firebase';
import { googleLogo } from '../../photos';
import { useLanguage } from '../../context/LanguageContext.jsx';
import { useTranslatedText } from '../i18n/useTranslatedText.js';
import { formatAuthErrorMessage } from '../../utils/authErrorMessage.js';
import './Auth.css';

const Signup = () => {
  const navigate = useNavigate();
  const { isEnglish } = useLanguage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [age, setAge] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const loadingText = useTranslatedText('Yükleniyor...');
  const titleText = useTranslatedText('Hesap Oluştur');
  const googleSignupButtonText = useTranslatedText('Gmail ile Hesap Oluştur');
  const loginText = useTranslatedText('Giriş Yap');
  const alreadyText = useTranslatedText('Zaten hesabınız var mı?');
  const emailPlaceholder = useTranslatedText('E-posta');
  const usernamePlaceholder = useTranslatedText('Kullanıcı Adı');
  const agePlaceholder = useTranslatedText('Yaş');
  const passwordPlaceholder = useTranslatedText('Şifre');
  const serviceUnavailableText = useTranslatedText('Kimlik doğrulama hizmeti geçici olarak kullanılamıyor. Lütfen daha sonra tekrar deneyin.');
  const rateLimitedText = useTranslatedText('Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.');
  const authFailedText = useTranslatedText('İşlem tamamlanamadı. Lütfen bilgilerinizi kontrol edip tekrar deneyin.');

  const authErrorMessages = {
    serviceUnavailable: serviceUnavailableText,
    rateLimited: rateLimitedText,
    fallback: authFailedText,
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setTimeout(() => navigate('/'), 500);
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await sendEmailVerification(user, { handleCodeInApp: true });

      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        username,
        age: parseInt(age, 10),
        createdAt: new Date().getTime(),
        isAdmin: false,
        emailVerified: false,
      });

      const startAt = new Date().getTime();
      const checkVerification = async () => {
        await user.reload();
        if (user.emailVerified) {
          await user.getIdToken(true);
          await setDoc(doc(db, 'users', user.uid), { emailVerified: true }, { merge: true });
          setMessage(isEnglish ? 'Email verified successfully.' : 'E-posta başarıyla doğrulandı.');
          navigate('/profil');
          return;
        }

        const elapsed = new Date().getTime() - startAt;
        if (elapsed > 15 * 60 * 1000) {
          await deleteUser(user);
          await deleteDoc(doc(db, 'users', user.uid));
          setMessage(
            isEnglish
              ? 'Account deleted due to missing verification. Please sign up again.'
              : 'Doğrulama yapılmadığı için hesap silindi. Lütfen tekrar kayıt olun.'
          );
          navigate('/hesap-oluştur');
          return;
        }

        const left = Math.max(0, (15 * 60 * 1000 - elapsed) / 1000);
        const minutes = Math.floor(left / 60);
        const seconds = Math.floor(left % 60);
        setMessage(
          isEnglish
            ? `Please verify your email. Time remaining: ${minutes}:${seconds < 10 ? '0' : ''}${seconds}.`
            : `Lütfen e-postanızı doğrulayın. Kalan süre: ${minutes}:${seconds < 10 ? '0' : ''}${seconds}.`
        );
        setTimeout(checkVerification, 1000);
      };

      checkVerification();
    } catch (err) {
      setError(formatAuthErrorMessage(err, authErrorMessages));
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await setDoc(userDocRef, {
          email: user.email,
          createdAt: new Date().getTime(),
          isAdmin: false,
          emailVerified: true,
        });
        navigate('/google-kayıt-tamamla');
      } else if (!userDocSnap.data().username || !userDocSnap.data().age) {
        navigate('/google-kayıt-tamamla');
      } else {
        navigate('/profil');
      }
    } catch (err) {
      setError(formatAuthErrorMessage(err, authErrorMessages));
    }
  };

  if (loading) return <div>{loadingText}</div>;

  return (
    <div className="auth-container">
      <h2>{titleText}</h2>
      {error && <div className="error-message">{error}</div>}
      {message && <div className="info-message">{message}</div>}
      <button className="google-signin" onClick={handleGoogleSignup}>
        <img src={googleLogo} alt="Google logo" className="google-logo" />
        {googleSignupButtonText}
      </button>
      <form onSubmit={handleSignup}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={emailPlaceholder} required />
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={usernamePlaceholder}
          required
          minLength="3"
          maxLength="20"
        />
        <input
          type="number"
          value={age}
          onChange={(e) => setAge(e.target.value)}
          placeholder={agePlaceholder}
          required
          min="13"
          max="120"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={passwordPlaceholder}
          required
        />
        <button type="submit" className="Confirm">{titleText}</button>
      </form>
      <div className="switch-auth">
        <p>{alreadyText}<a href="/giriş-yap">{loginText}</a></p>
      </div>
    </div>
  );
};

export default Signup;
